import { StatusType, datadogLogs } from '@datadog/browser-logs';
import { isObject } from 'lodash-es';

// import { initDatadogRum } from 'hooks/rum/init-datadog-rum';
import { getStaticPathAndSearch } from 'navigation/location/location';
import { appVersion } from 'utils/app-version-info';
import { getDeviceIdAsync } from 'utils/device-id';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';

import { brand, env, getConfigValue, platform } from '../environment';

import { LogLevel } from './types';
import { IDatadogLogger, UNKNOWN_ERROR_MESSAGE } from './types';

export { StatusType } from '@datadog/browser-logs';

export function initDatadog() {
  initDatadogLogging();
  // initDatadogRum(); // disabled for now, see init-datadog-rum.web.ts
}

function initDatadogLogging(): void {
  const dataDogConfig = getConfigValue({ key: 'datadog', defaultValue: {} });
  const clientToken = dataDogConfig.webClientToken;

  datadogLogs.init({
    clientToken,
    // TODO revisit this property. All console.errors go to datadog and it is sometimes duplicative with other logging we have.
    forwardErrorsToLogs: true,
    env: __DEV__ ? 'localhost' : env(),
    service: `${brand()}_${platform()}`,
    version: appVersion,
    // add "beforeSend" to filter / sample logs in the future if needed
  });
}

getDeviceIdAsync().then(deviceId => addContext('deviceId', deviceId));

export function dataDogLogger({ message, context = {}, status }: IDatadogLogger): void {
  const ctx: any = {
    ...context,
    pageLocation: getStaticPathAndSearch(),
    userId: syncStorage.getString(SyncStorageKey.USER_ID) ?? undefined,
  };

  const err = ctx.error;

  if (err && isObject(err) && (err as any)?.message) {
    // this is likely not a JS error object, so assign it to a different field or it wont show up in DD
    ctx.errorObj = err;
  }

  const strMessage: string = (() => {
    if (typeof message === 'string') {
      return message;
    }

    if (ctx?.error?.message) {
      return ctx?.error?.message;
    }

    if (ctx?.error) {
      return UNKNOWN_ERROR_MESSAGE;
    }

    return 'Unknown message';
  })();

  datadogLogs.logger.log(strMessage, ctx, getMappedLevel(status));
}

const getMappedLevel = (status: LogLevel): StatusType => {
  if (status === LogLevel.trace || status === LogLevel.debug) {
    return 'debug';
  }
  if (status === LogLevel.info) {
    return 'info';
  }
  if (status === LogLevel.warn) {
    return 'warn';
  }
  if (status === LogLevel.error || status === LogLevel.fatal) {
    return 'error';
  }
  return 'info';
};

export function addContext(key: string, value: string) {
  datadogLogs.logger.setContext({ ...datadogLogs.logger.getContext(), [key]: value });
}
